import { Button, Input } from '@/lib';
import { SpinnerIcon } from '@/lib/Spinner';
import { TablePagination } from '@/lib/Tables/parts/TablePagination/TablePagination';
import { DataSourceIDEnum, type DataSource, type DataSourceCodeConfig } from '@/models/dataSource';
import { useSaveDataSourceMutation } from '@/services/dataSources';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LuDownload, LuExternalLink, LuQrCode, LuTrash } from 'react-icons/lu';
import { useWorkspace } from '@/features/workspaces/hooks';
import { CitizensCount } from '../components/CitizensCount';
import { useComuniLists, type ComuniList } from '../useComuniLists';
import { isCitizenFormList } from '../listKind';
import { DeleteCitizenFormDialog } from './DeleteCitizenFormDialog';
import { bareMunicipalityName, citizenFormUrl } from './registration';

const FORMS_PAGE_SIZE = 5;

export const CitizenFormsTab = () => {
  const { t } = useTranslation('comuni');
  const workspace = useWorkspace();
  const { lists, isLoading, isError, refetch } = useComuniLists();
  const [saveDataSource, { isLoading: creating }] = useSaveDataSourceMutation();
  const [selected, setSelected] = useState<DataSource<DataSourceCodeConfig>>();
  const [qr, setQr] = useState('');
  const [qrFailed, setQrFailed] = useState(false);
  const [qrAttempt, setQrAttempt] = useState(0);
  const [error, setError] = useState('');
  const [pageNo, setPageNo] = useState(0);
  const [toDelete, setToDelete] = useState<ComuniList>();
  const forms = lists.filter((list) => isCitizenFormList(list.audience, list.datasources));
  const pageForms = forms.slice(pageNo * FORMS_PAGE_SIZE, (pageNo + 1) * FORMS_PAGE_SIZE);
  const url = selected ? citizenFormUrl(selected.id) : '';

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(forms.length / FORMS_PAGE_SIZE) - 1);
    setPageNo((current) => (current > lastPage ? lastPage : current));
  }, [forms.length]);

  useEffect(() => {
    let cancelled = false;
    setQr('');
    setQrFailed(false);
    if (url)
      QRCode.toDataURL(url, { width: 1024, margin: 4, errorCorrectionLevel: 'M' })
        .then((value) => {
          if (!cancelled) setQr(value);
        })
        .catch(() => {
          if (!cancelled) setQrFailed(true);
        });
    return () => {
      cancelled = true;
    };
  }, [url, qrAttempt]);

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creating) return;
    const data = new FormData(event.currentTarget);
    setError('');
    try {
      const result = await saveDataSource({
        workspaceId: workspace.id,
        dataSource: {
          name: String(data.get('name')).trim(),
          type: DataSourceIDEnum.WebsiteJS,
          configuration: {
            url: window.location.origin,
            citizenForm: {
              municipalityName: bareMunicipalityName(String(data.get('municipalityName'))),
              privacyUrl: String(data.get('privacyUrl')).trim(),
            },
          },
        },
      }).unwrap();
      setSelected(result.dataSource as DataSource<DataSourceCodeConfig>);
      refetch();
    } catch {
      setError(t('citizenForms.createError'));
    }
  };

  return (
    <section className='pt-6'>
      <h2 className='text-2xl font-semibold tracking-tight text-primary-900'>{t('citizenForms.title')}</h2>
      <p className='mt-2 max-w-3xl text-sm leading-relaxed text-tertiary-600'>{t('citizenForms.description')}</p>
      <div className='mt-7 grid items-start gap-10 lg:grid-cols-2'>
        <div>
          <form onSubmit={create} className='grid gap-5'>
            <fieldset disabled={creating} className='grid gap-5'>
              <div>
                <label htmlFor='form-list-name' className='mb-2 block text-sm font-semibold text-primary-900'>
                  {t('citizenForms.listName')}
                </label>
                <Input id='form-list-name' name='name' defaultValue={t('citizenForms.defaultName')} required minLength={3} maxLength={50} pattern='.*\S.*' />
              </div>
              <div>
                <label htmlFor='form-municipality' className='mb-2 block text-sm font-semibold text-primary-900'>
                  {t('citizenForms.municipalityName')}
                </label>
                <Input
                  id='form-municipality'
                  name='municipalityName'
                  defaultValue={bareMunicipalityName(workspace.name)}
                  placeholder='Bassano del Grappa'
                  required
                  maxLength={160}
                  pattern='.*\S.*'
                />
              </div>
              <div>
                <label htmlFor='form-privacy-url' className='mb-2 block text-sm font-semibold text-primary-900'>
                  {t('citizenForms.privacyUrl')}
                </label>
                <Input
                  id='form-privacy-url'
                  name='privacyUrl'
                  type='url'
                  placeholder='https://www.comune.it/privacy'
                  required
                  maxLength={2048}
                  pattern='https?://.+'
                />
              </div>
            </fieldset>
            <Button type='submit' intent='primary' size='lg' isLoading={creating} className='justify-self-start'>
              <LuQrCode className='size-5' />
              {t('citizenForms.create')}
            </Button>
          </form>
          {error && (
            <p className='mt-4 text-sm text-red-700' role='alert'>
              {error}
            </p>
          )}
          <h3 className='mb-3 mt-10 text-lg font-semibold text-primary-900'>{t('citizenForms.saved')}</h3>
          {isLoading ? (
            <div role='status' aria-label={t('publicForm.loading')}>
              <SpinnerIcon className='size-6 text-brand-700' />
            </div>
          ) : isError ? (
            <div role='alert'>
              <p className='mb-2 text-sm text-red-700'>{t('lists.loadError')}</p>
              <Button intent='secondary' onClick={refetch}>
                {t('lists.retry')}
              </Button>
            </div>
          ) : forms.length ? (
            <>
              <ul className='divide-y divide-primary-border'>
                {pageForms.map((list) => (
                  <li key={list.id} className='flex items-center gap-2'>
                    <button
                      type='button'
                      onClick={() => {
                        setSelected(list.datasources[0] as DataSource<DataSourceCodeConfig>);
                        setQrAttempt((attempt) => attempt + 1); // selecting the form again retries a failed QR
                        setError('');
                      }}
                      aria-pressed={selected?.id === list.datasources[0]?.id}
                      className='flex min-h-12 min-w-0 flex-1 items-center justify-between gap-4 py-3 text-left text-sm font-medium text-primary-900 hover:text-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-700'
                    >
                      <span className='min-w-0'>
                        <span className='block break-words'>{list.name}</span>
                        <span className='block text-xs font-normal text-tertiary-600'>
                          <CitizensCount datasourceId={list.datasources[0]?.id} metric='events' /> {t('citizenForms.submissions')}
                        </span>
                      </span>
                      <LuQrCode className='size-5 shrink-0 text-brand-700' aria-hidden='true' />
                    </button>
                    <button
                      type='button'
                      onClick={() => setToDelete(list)}
                      aria-label={t('citizenForms.deleteLabel', { name: list.name })}
                      className='flex size-11 shrink-0 items-center justify-center rounded-lg text-tertiary-600 hover:bg-red-50 hover:text-red-700 focus-visible:outline-2 focus-visible:outline-brand-700'
                    >
                      <LuTrash className='size-5' aria-hidden='true' />
                    </button>
                  </li>
                ))}
              </ul>
              {forms.length > FORMS_PAGE_SIZE && (
                <TablePagination
                  className='mt-4'
                  limit={FORMS_PAGE_SIZE}
                  currentPage={pageNo + 1}
                  totalAmount={forms.length}
                  onPageClick={setPageNo}
                />
              )}
            </>
          ) : (
            <p className='text-sm text-tertiary-600'>{t('citizenForms.empty')}</p>
          )}
        </div>
        {selected ? (
          <div className='rounded-xl border border-primary-border p-6'>
            <h3 className='text-xl font-bold text-primary-900'>{selected.name}</h3>
            <p className='mt-1 text-sm font-medium text-primary-900'>
              <CitizensCount datasourceId={selected.id} metric='events' /> {t('citizenForms.submissions')}
            </p>
            <p className='mb-4 mt-2 text-sm text-tertiary-600'>{t('citizenForms.qrDescription')}</p>
            {qr ? (
              <img src={qr} alt={t('citizenForms.qrAlt', { name: selected.name })} className='mx-auto h-auto w-full max-w-[280px]' width={1024} height={1024} />
            ) : qrFailed ? (
              <p className='text-sm text-red-700' role='alert'>
                {t('citizenForms.qrError')}
              </p>
            ) : (
              <SpinnerIcon className='mx-auto size-8 text-brand-700' />
            )}
            <a href={url} target='_blank' rel='noopener noreferrer' className='mt-4 block break-all text-sm text-brand-700 underline underline-offset-4'>
              {url}
            </a>
            <div className='mt-5 flex flex-wrap items-center gap-4'>
              {qr && (
                <a
                  href={qr}
                  download={`qr-${selected.id}.png`}
                  className='inline-flex min-h-11 items-center gap-2 rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700'
                >
                  <LuDownload className='size-4' />
                  {t('citizenForms.download')}
                </a>
              )}
              <a
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-brand-700 underline underline-offset-4'
              >
                <LuExternalLink className='size-4' />
                {t('citizenForms.open')}
              </a>
            </div>
          </div>
        ) : (
          <div className='border-t border-primary-border py-6 text-sm leading-relaxed text-tertiary-600'>{t('citizenForms.previewEmpty')}</div>
        )}
      </div>
      {toDelete && (
        <DeleteCitizenFormDialog
          form={toDelete}
          onClose={() => setToDelete(undefined)}
          onDeleted={() => {
            if (selected?.id === toDelete.datasources[0]?.id) setSelected(undefined);
            setToDelete(undefined);
            refetch();
          }}
        />
      )}
    </section>
  );
};
